import { useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ChevronRight, Users } from "lucide-react";
import { services } from "@/data/services";
import { Reveal, MaskedLines, Eyebrow } from "@/components/Reveal";
import CTABand from "@/components/CTABand";
import PricingSection from "@/components/PricingSection";
import Seo from "@/components/Seo";

export default function ServicePage() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  if (!service) return <Navigate to="/" replace />;

  return (
    <div data-testid={`service-page-${service.slug}`}>
      <Seo title={service.seoTitle} description={service.seoDesc} />

      {/* Hero */}
      <section ref={heroRef} className="relative flex items-end overflow-hidden bg-[#043B1C] pt-[76px]" data-testid="service-hero">
        <motion.img
          src={service.image}
          alt={service.name}
          style={{ y: heroY }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#043B1C]/90 via-[#043B1C]/45 to-[#043B1C]/30" />
        <div className="relative mx-auto w-full max-w-[1160px] px-5 pb-6 pt-8 md:px-8 md:pb-10 md:pt-14">
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-white/60 md:text-xs"
            data-testid="service-breadcrumb"
          >
            <Link to="/" className="transition-colors hover:text-[#F4BE36]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/#services" className="transition-colors hover:text-[#F4BE36]">Services</Link>
            <ChevronRight size={12} />
            <span className="text-white/90">{service.name}</span>
          </motion.nav>
          <h1 className="mt-3 max-w-3xl text-2xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-4xl md:mt-4 md:text-5xl" data-testid="service-headline">
            <MaskedLines lines={[service.name]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-3 max-w-xl text-sm leading-[1.6] text-white/80 md:mt-5 md:text-lg"
            data-testid="service-description"
          >
            {service.heroDesc}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
          >
            <Link
              to="/contact"
              data-testid="service-estimate-button"
              className="mt-4 inline-block rounded-full bg-[#F4BE36] px-6 py-2.5 text-xs font-bold text-[#043B1C] transition-colors duration-300 hover:bg-white md:mt-8 md:px-8 md:py-4 md:text-sm"
            >
              Get an Estimate
            </Link>
          </motion.div>
        </div>
      </section>

      {service.slug === "spring-fall-clean-ups" && <PricingSection showLabel={false} />}

      {/* What's included — hidden during the fall promotion via hideIncluded flag; set it false to restore */}
      {!service.hideIncluded && (
      <section className="bg-[#F4F6F3]" data-testid="service-included">
        <div className="mx-auto max-w-[1160px] px-5 py-16 md:px-8 md:py-24">
          <Reveal>
            <Eyebrow>What's included</Eyebrow>
            <h2 className="mt-4 text-3xl font-extrabold leading-[1.15] tracking-tight text-[#16241C] md:text-[2.2rem]">
              What you get
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {service.included.map((item, i) => (
              <Reveal key={item} delay={i * 0.08}>
                <div className="flex h-full items-start gap-4 rounded-2xl border border-[#16241C]/10 bg-white p-6" data-testid={`included-card-${i}`}>
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#043B1C]">
                    <Check size={14} strokeWidth={3} className="text-[#F4BE36]" />
                  </span>
                  <p className="text-base font-bold leading-[1.5] text-[#16241C]">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* How it works */}
      <section className="bg-white" data-testid="service-process">
        <div className="mx-auto max-w-[1160px] px-5 py-16 md:px-8 md:py-24">
          <Reveal>
            <Eyebrow>How it works</Eyebrow>
            <h2 className="mt-4 text-3xl font-extrabold leading-[1.15] tracking-tight text-[#16241C] md:text-[2.2rem]">
              The process, start to finish
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {service.process.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.08}>
                <div className="border-t-2 border-[#043B1C] pt-5" data-testid={`service-process-step-${i}`}>
                  <p className="text-base font-bold text-[#16241C]">
                    <span className="mr-2 text-[#F4BE36]">{i + 1}.</span>
                    {step.title}
                  </p>
                  <p className="mt-2 text-sm leading-[1.6] text-[#5B6B60]">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Secondary photo */}
      {service.image2 && (
        <section className="bg-white pb-16 md:pb-24" data-testid="service-secondary-photo">
          <div className="mx-auto max-w-[1160px] px-5 md:px-8">
            <Reveal>
              <img
                src={service.image2}
                alt={`${service.name} crew at work`}
                className="h-[320px] w-full rounded-2xl object-cover md:h-[480px]"
                data-testid="service-secondary-image"
              />
            </Reveal>
          </div>
        </section>
      )}

      {/* Who it's for + pricing */}
      <section className="bg-white pb-20 md:pb-28">
        <div className="mx-auto grid max-w-[1160px] gap-6 px-5 md:px-8 lg:grid-cols-2 lg:gap-8">
          <Reveal>
            <div className="h-full rounded-2xl border border-[#16241C]/10 bg-[#F4F6F3] p-8" data-testid="service-who-for">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#043B1C]">
                <Users size={19} className="text-[#F4BE36]" strokeWidth={2.2} />
              </span>
              <h2 className="mt-5 text-xl font-extrabold tracking-tight text-[#16241C]">Who it's for</h2>
              <p className="mt-3 text-base leading-[1.6] text-[#5B6B60]">{service.whoFor}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-[#16241C]/10 bg-[#F4F6F3] p-8" data-testid="service-pricing-note">
              <h2 className="text-xl font-extrabold tracking-tight text-[#16241C]">How pricing works</h2>
              <p className="mt-3 text-base leading-[1.6] text-[#5B6B60]">{service.pricingNote}</p>
              <Link
                to="/contact"
                data-testid="pricing-estimate-button"
                className="mt-6 inline-block rounded-full bg-[#043B1C] px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#F4BE36] hover:text-[#043B1C]"
              >
                Request an Estimate
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        heading={`Ready to talk about ${service.name.toLowerCase()}?`}
        subtext="Reach out and we'll get back to you about your property."
      />
    </div>
  );
}
